// `Apparence` settings section (US-184, US-185 / US-046, US-047, US-048).
//
// Theme switcher is the centerpiece — three preview cards (Sombre / Clair /
// Système). Active card gets a `2px var(--accent)` border + "✓ Activé"
// label. We use `useTheme()` from `@barel/ui` so the change persists to
// localStorage; we also call `useUpdateProfile()` to mirror the choice
// server-side so the extension picks it up on next sync.
//
// "Réduire les animations" is the second toggle — backed by
// `useReducedMotion()` (localStorage) + `useUpdateProfile({ reduceMotion })`.

import { useEffect, useState } from 'react';

import { useUpdateProfile } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Switch, useReducedMotion, useTheme } from '@barel/ui';
import type { Theme } from '@barel/ui';
import { Check } from 'lucide-react';

interface ThemePreview {
  id: Theme;
  labelKey: 'themeDark' | 'themeLight' | 'themeSystem';
  bg: string;
  panel: string;
  accent: string;
}

const THEME_PREVIEWS: ThemePreview[] = [
  { id: 'dark', labelKey: 'themeDark', bg: '#0c0c0f', panel: '#141418', accent: '#5b6ef8' },
  { id: 'light', labelKey: 'themeLight', bg: '#f7f7fa', panel: '#ffffff', accent: '#4f5fde' },
  { id: 'system', labelKey: 'themeSystem', bg: '#1a1a26', panel: '#ffffff', accent: '#6f7eff' },
];

export function Apparence() {
  const t = useT('settings');
  const { theme, setTheme } = useTheme();
  const { motion, setMotion } = useReducedMotion();
  const update = useUpdateProfile();
  // Only mirror to the server while the user is interacting; avoid the
  // initial-render write-amplification.
  const [hydrated, setHydrated] = useState<boolean>(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const handleThemeChange = (next: Theme): void => {
    setTheme(next);
    if (!hydrated) return;
    void update.mutateAsync({ themeMode: next });
  };

  const handleMotionChange = (reduced: boolean): void => {
    setMotion(reduced ? 'reduce' : 'auto');
    if (!hydrated) return;
    void update.mutateAsync({ reduceMotion: reduced });
  };

  return (
    <section
      data-section="appearance"
      aria-labelledby="settings-appearance-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1 border-b border-default pb-4">
        <h1 id="settings-appearance-title" className="text-lg font-semibold text-primary">
          {t('appearance.title')}
        </h1>
        <p className="text-xs text-tertiary">{t('appearance.subtitle')}</p>
      </header>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-primary">{t('appearance.themeLabel')}</span>
          <span className="text-xs text-tertiary">{t('appearance.themeHint')}</span>
        </div>
        <div className="grid grid-cols-3 gap-3" data-testid="settings-theme-cards">
          {THEME_PREVIEWS.map((preview) => {
            const isActive = preview.id === theme;
            return (
              <button
                key={preview.id}
                type="button"
                role="radio"
                aria-checked={isActive}
                aria-label={t(`appearance.${preview.labelKey}`)}
                data-active={isActive || undefined}
                data-testid={`settings-theme-${preview.id}`}
                onClick={() => handleThemeChange(preview.id)}
                className="ls-focus-ring rounded-xl border-2 border-default bg-transparent p-1 text-left transition-colors hover:border-accent-border data-[active=true]:border-accent"
              >
                <div
                  className="flex h-[88px] gap-2 rounded-lg p-2"
                  style={{ background: preview.bg }}
                  aria-hidden="true"
                >
                  <div
                    className="flex w-6 flex-col items-center gap-1 rounded-md p-1.5"
                    style={{ background: preview.panel }}
                  >
                    <span
                      className="block h-2 w-2 rounded-sm"
                      style={{ background: preview.accent }}
                    />
                    <span className="block h-2 w-2 rounded-sm bg-secondary/40" />
                    <span className="block h-2 w-2 rounded-sm bg-secondary/40" />
                  </div>
                  <div className="flex flex-1 flex-col justify-end gap-1.5">
                    <span className="block h-1.5 w-1/2 rounded-sm bg-secondary/40" />
                    <span className="block h-1 w-3/4 rounded-sm bg-secondary/30" />
                    <span className="block h-1 w-2/3 rounded-sm bg-secondary/30" />
                    <span
                      className="block h-3 w-1/3 rounded-sm"
                      style={{ background: preview.accent }}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between px-1.5 pt-2">
                  <span className="text-sm font-semibold text-primary">
                    {t(`appearance.${preview.labelKey}`)}
                  </span>
                  {isActive ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent">
                      <Check size={12} />
                      {t('appearance.themeActive')}
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Row
        label={t('appearance.systemThemeLabel')}
        hint={t('appearance.systemThemeHint')}
        control={
          <Switch
            checked={theme === 'system'}
            onCheckedChange={(value) => handleThemeChange(value ? 'system' : 'dark')}
            aria-label={t('appearance.systemThemeLabel')}
            data-testid="settings-system-theme"
          />
        }
      />

      <Row
        label={t('appearance.reduceMotionLabel')}
        hint={t('appearance.reduceMotionHint')}
        control={
          <Switch
            checked={motion === 'reduce'}
            onCheckedChange={(value) => handleMotionChange(value === true)}
            aria-label={t('appearance.reduceMotionLabel')}
            data-testid="settings-reduce-motion"
          />
        }
      />
    </section>
  );
}

interface RowProps {
  label: string;
  hint?: string;
  control: React.ReactNode;
}

function Row({ label, hint, control }: RowProps) {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-subtle py-3">
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium text-primary">{label}</span>
        {hint ? <span className="text-xs text-tertiary">{hint}</span> : null}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}
